import React from 'react';

interface Company {
  company_id: string;
  name: string;
  company_code: string;
  created_at: string;
  created_by_username: string;
  assigned_user?: number[];
  assigned_usernames?: string | null;
}

interface FRUpdateCompanyListProps {
  companies: Company[];
  isLoading: boolean;
  onSelectCompany: (company: Company) => void;
  onDeleteCompany: (companyId: string) => void;
  onAddCompany: () => void;
  onOpenTagCleaner: () => void;
  onEditCompanyCode: (company: Company) => void;
  canManage: boolean;
}

const FRUpdateCompanyList: React.FC<FRUpdateCompanyListProps> = ({
  companies,
  isLoading,
  onSelectCompany,
  onDeleteCompany,
  onAddCompany,
  onOpenTagCleaner,
  onEditCompanyCode,
  canManage
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-full w-full relative">
      {/* Unified Container with Background */}
      <div className="min-h-full relative overflow-hidden rounded-2xl bg-white/20 dark:bg-gray-900/20 backdrop-blur-3xl border border-white/40 dark:border-white/20 shadow-2xl flex flex-col"
        style={{
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
        }}
      >
        
        {/* Content Layer */}
        <div className="relative z-10 flex-1 flex flex-col min-h-0">
          {/* Header Section */}
          <div className="flex-shrink-0 p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  İçerik Güncelleme
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                  Güncelleme yapmak için bir şirket seçin
                </p>
              </div>
              <div className="flex items-center gap-3">
                {/* Etiket Temizleyici Butonu */}
                <button
                  onClick={onOpenTagCleaner}
                  className="flex items-center justify-center gap-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-4 sm:px-5 py-2.5 rounded-lg font-semibold transition-all duration-200 shadow-md hover:shadow-lg hover:scale-[1.02] border border-orange-400/30"
                  title="Etiketlenmiş dosyaları temizle"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"></path>
                  </svg>
                  <span className="hidden sm:inline">Etiket Temizleyici</span>
                </button>
                
                {/* Yeni Şirket Butonu */}
                {canManage && (
                  <button
                    onClick={onAddCompany}
                    className="flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-4 sm:px-5 py-2.5 rounded-lg font-semibold transition-all duration-200 shadow-md hover:shadow-lg hover:scale-[1.02] border border-blue-500/30"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                    </svg>
                    <span className="hidden sm:inline">Yeni Şirket</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Separator */}
          <div className="flex-shrink-0 px-6">
            <div className="h-px bg-gradient-to-r from-transparent via-white/30 dark:via-white/20 to-transparent"></div>
          </div>

          {/* Content Area - Şirketler */}
          <div className="flex-1 overflow-y-auto p-6 pt-4 thin-scrollbar">

            {/* Loading State */}
            {isLoading && (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-4">
                <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <p className="text-gray-600 dark:text-gray-400 font-medium">Şirketler yükleniyor...</p>
              </div>
            </div>
          )}

            {/* Company Grid */}
            {!isLoading && companies.length > 0 && (
            <div>
              {/* Şirketler Başlığı */}
              <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-3 drop-shadow-sm">
                  <div className="w-10 h-10 rounded-xl bg-blue-500/40 dark:bg-blue-500/50 backdrop-blur-sm flex items-center justify-center border border-blue-500/50 dark:border-blue-400/50 shadow-lg shadow-blue-500/20">
                    <svg className="w-5 h-5 text-blue-700 dark:text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                    </svg>
                  </div>
                  Şirketler
                  <span className="px-3 py-1 text-sm font-semibold rounded-lg bg-blue-500/30 dark:bg-blue-500/40 text-blue-700 dark:text-blue-200 border border-blue-500/40 dark:border-blue-400/50 shadow-md shadow-blue-500/20">
                    {companies.length}
                  </span>
                </h2>
              </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {companies.map((company) => (
                <div
                  key={company.company_id}
                  className="group bg-white/50 dark:bg-gray-800/50 backdrop-blur-2xl rounded-2xl border border-white/60 dark:border-white/30 p-4 hover:bg-white/70 dark:hover:bg-gray-800/70 hover:border-blue-400/70 dark:hover:border-blue-500/60 transition-all duration-300 cursor-pointer relative shadow-2xl hover:shadow-blue-500/30 hover:scale-[1.03]"
                  onClick={() => onSelectCompany(company)}
                  style={{
                    WebkitFontSmoothing: 'antialiased',
                    backfaceVisibility: 'hidden',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.4)'
                  }}
                >
                  {/* Action Buttons - Sadece Admin görebilir */}
                  {canManage && (
                    <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      {/* Edit Code Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditCompanyCode(company);
                        }}
                        className="w-7 h-7 flex items-center justify-center rounded-lg bg-blue-500/30 dark:bg-blue-500/40 backdrop-blur-sm border border-blue-500/50 text-blue-600 dark:text-blue-400 transition-all duration-200 hover:bg-blue-500/50 dark:hover:bg-blue-500/60 hover:scale-110 shadow-lg shadow-blue-500/20"
                        title="Şirket Kodunu Düzenle"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                        </svg>
                      </button>
                      
                      {/* Delete Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteCompany(company.company_id);
                        }}
                        className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-500/30 dark:bg-red-500/40 backdrop-blur-sm border border-red-500/50 text-red-600 dark:text-red-400 transition-all duration-200 hover:bg-red-500/50 dark:hover:bg-red-500/60 hover:scale-110 shadow-lg shadow-red-500/20"
                        title="Şirketi Sil"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                        </svg>
                      </button>
                    </div>
                  )}

                  {/* Company Icon */}
                  <div className="w-12 h-12 bg-blue-500/40 dark:bg-blue-500/50 backdrop-blur-sm rounded-xl flex items-center justify-center mb-3 border border-blue-500/50 dark:border-blue-400/50 shadow-lg shadow-blue-500/20 group-hover:shadow-blue-500/40 group-hover:border-blue-400/70 transition-all">
                    <span className="text-lg font-bold text-blue-700 dark:text-blue-300 drop-shadow-lg">
                      {company.company_code.substring(0, 2).toUpperCase()}
                    </span>
                  </div>

                  {/* Company Info */}
                  <div className="space-y-2">
                    {/* Name */}
                    <h3 className="text-base font-bold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-300 transition-colors line-clamp-1 drop-shadow-sm">
                      {company.name}
                    </h3>
                    
                    {/* Code */}
                    <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold bg-blue-500/30 dark:bg-blue-500/40 text-blue-700 dark:text-blue-200 border border-blue-500/40 dark:border-blue-400/50 shadow-md shadow-blue-500/20 backdrop-blur-sm">
                      {company.company_code}
                    </span>

                    {/* Created By */}
                    <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                      </svg>
                      <span className="truncate">Oluşturan: {company.created_by_username}</span>
                    </div>

                    {/* Assigned Users */}
                    {company.assigned_usernames && (
                      <div className="flex items-start gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                        <svg className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                        </svg>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap gap-1">
                            {company.assigned_usernames.split(',').map((username, idx) => {
                              const trimmedUsername = username.trim();
                              return trimmedUsername ? (
                                <span 
                                  key={idx}
                                  className="inline-flex items-center px-1.5 py-0.5 bg-green-500/20 dark:bg-green-500/30 text-green-700 dark:text-green-300 rounded text-[10px] font-medium border border-green-500/30"
                                >
                                  {trimmedUsername}
                                </span>
                              ) : null;
                            })}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Created Date */}
                    <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-500">
                      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                      </svg>
                      <span className="truncate">{formatDate(company.created_at)}</span>
                    </div>
                  </div>

                  {/* Hover Indicator */}
                  <div className="mt-3 pt-3 border-t border-white/40 dark:border-white/20">
                    <div className="flex items-center justify-center gap-1.5 font-semibold text-sm text-blue-700 dark:text-blue-300 group-hover:text-blue-600 dark:group-hover:text-blue-200 transition-colors drop-shadow-sm">
                      <span>Seç</span>
                      <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"></path>
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
              </div>
            </div>
          )}

            {/* Empty State */}
            {!isLoading && companies.length === 0 && (
            <div className="flex items-center justify-center h-full">
              <div 
                className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-2xl rounded-2xl border border-white/60 dark:border-white/30 p-12 text-center max-w-lg shadow-2xl"
                style={{
                  WebkitFontSmoothing: 'antialiased',
                  backfaceVisibility: 'hidden',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.4)'
                }}
              >
                <div className="w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  {canManage ? 'Henüz şirket eklenmemiş' : 'Size atanmış şirket bulunmuyor'}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  {canManage ? 'Başlamak için yeni bir şirket ekleyin' : 'Yöneticiniz size şirket ataması yaptığında burada görünecektir'}
                </p>
                {canManage && (
                  <button
                    onClick={onAddCompany}
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 shadow-md hover:shadow-lg hover:scale-[1.02] border border-blue-500/30"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                    </svg>
                    Yeni Şirket Ekle
                  </button>
                )}
              </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FRUpdateCompanyList;

